import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGetUserID } from '../hooks/useGetUserID';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useCookies } from 'react-cookie';
import RecipeImage from '../components/RecipeImage';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MealPrepDraft() {
    const [cookies] = useCookies(['access_token']);
    const [recipes, setRecipes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const userID = useGetUserID();
    const navigate = useNavigate();

    const getAuthToken = () => cookies.access_token || window.localStorage.getItem('auth_token');
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    useEffect(() => {
        if (!userID || !getAuthToken()) {
            navigate('/auth/login');
            return;
        }

        const fetchDraftRecipes = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(`${API_BASE_URL}/recipes/meal-prep-draft/${userID}`, {
                    headers: getAuthHeaders()
                });
                if (response.data && response.data.mealPrepDraft) {
                    setRecipes(response.data.mealPrepDraft);
                } else {
                    setRecipes([]);
                }
            } catch (error) {
                console.error('Error fetching meal prep draft:', error);
                if (error.response?.status === 401) {
                    navigate('/auth/login');
                } else {
                    setRecipes([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchDraftRecipes();
    }, [userID, navigate]);

    const removeFromDraft = async (recipeID, e) => {
        if (e) e.stopPropagation();
        try {
            await axios.put(`${API_BASE_URL}/recipes/meal-prep-draft/remove`,
                { recipeID, userID },
                { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() } }
            );
            setRecipes(prev => prev.filter(r => r._id !== recipeID));
            alert('Removed from meal prep draft');
        } catch (error) {
            console.error('Error removing from draft:', error);
            alert(error.response?.data?.message || 'Error removing from draft');
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <LoadingSpinner size="xl" color="primary" text="Loading your meal prep draft..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                <div className="mb-4 sm:mb-6">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Meal prep draft</h1>
                    <p className="text-sm text-gray-600 mt-0.5">
                        Recipes you’ve added here. Use them when building your meal plan.
                    </p>
                </div>

                {recipes.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 sm:p-8 text-center">
                        <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-amber-100 flex items-center justify-center">
                            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h2 className="text-sm font-semibold text-gray-900 mb-1.5">No recipes in your draft</h2>
                        <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                            When you tap the plus on a recipe, choose “Add to Meal prep draft” to save it here.
                        </p>
                        <button
                            onClick={() => navigate('/searchrecipes')}
                            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                        >
                            Search recipes
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                        {recipes.map((recipe) => (
                            <div
                                key={recipe._id}
                                onClick={() => navigate(`/recipe/${recipe._id}`)}
                                className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden bg-gray-200">
                                    <RecipeImage
                                        imageUrl={recipe.imageUrl}
                                        alt={recipe.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    />
                                    <div className="absolute top-2 right-2">
                                        <button
                                            onClick={(e) => removeFromDraft(recipe._id, e)}
                                            className="w-8 h-8 bg-white/95 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                                            aria-label="Remove from meal prep draft"
                                        >
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="p-2 sm:p-2.5">
                                    <h3 className="font-medium text-xs sm:text-sm text-gray-900 line-clamp-2 leading-tight">
                                        {recipe.name}
                                    </h3>
                                    <div className="flex items-center text-xs text-gray-600 mt-2">
                                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {recipe.cookingTime} mins
                                        <span className="mx-2">·</span>
                                        <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        {recipe.servings || 1} servings
                                    </div>
                                    {recipe.createdBy && (
                                        <p className="text-xs text-gray-500 mt-1">{recipe.createdBy}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
